import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Package, Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      const user = await login(email, password);
      
      if (user.role !== 'admin') {
        toast.error('Acesso restrito a administradores');
        return;
      }
      
      toast.success('Bem-vindo ao painel administrativo!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl gradient-hero flex items-center justify-center">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white mb-1">
            Painel Administrativo
          </h1>
          <p className="text-slate-400">KITS TOP de ALIMENTOS</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-800 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@kitstop.com"
                className="rounded-xl bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                required
                data-testid="admin-login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-xl bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 pr-10"
                  required
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>

            <div className="text-center pt-1">
              <Link
                to="/esqueci-senha"
                className="text-sm text-slate-300 hover:text-white hover:underline"
                data-testid="admin-forgot-password-link"
              >
                Esqueci minha senha
              </Link>
            </div>
          </form>

          <div className="mt-6 p-4 bg-slate-700/50 rounded-xl">
            <p className="text-xs text-slate-400 text-center">
              Para recuperar acesso, use a opção "Esqueci minha senha"
            </p>
          </div>
        </div>

        <p className="text-center text-slate-400 text-sm mt-4">
          <Link to="/" className="hover:text-white transition-colors">
            ← Voltar para a loja
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
